import React from 'react';
import TalentCard from './TalentCard';
import { Box, Grid, Typography, Skeleton, Card, Stack } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IWorkProfile } from '../../types/global/helper';

const SKELETON_COUNT = 8;

export interface ITalentCardsProps {
    selectedCategory?: string;
    talents: IWorkProfile[];
    loading?: boolean;
    isMobile?: boolean;
}

const TalentCardSkeleton = ({ isMobile }: { isMobile?: boolean }) => {
    const { themeStyle } = useThemeContext();
    return (
        <Card
            sx={{
                borderRadius: 3,
                boxShadow: 2,
                width: '100%',
                maxWidth: '100%',
                minHeight: isMobile ? 320 : 420,
                p: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                bgcolor: theme[themeStyle].secondaryBg,
            }}>
            {/* Featured badge */}
            <Box
                sx={{
                    bgcolor: '#dea4a4',
                    width: isMobile ? 50 : 60,
                    height: isMobile ? 15 : 18,
                    borderRadius: 1,
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 2,
                }}>
                <Skeleton
                    variant="text"
                    width={isMobile ? 40 : 50}
                    height={isMobile ? 13 : 16}
                    sx={{ bgcolor: 'rgba(255,255,255,0.3)' }}
                />
            </Box>
            {/* Image carousel area */}
            <Skeleton
                variant="rectangular"
                width="100%"
                height={isMobile ? 150 : 200}
                sx={{ bgcolor: theme[themeStyle].grey, borderRadius: '16px 16px 0 0' }}
            />
            {/* Content */}
            <Box sx={{ p: isMobile ? 1.5 : 2, pb: 1, bgcolor: theme[themeStyle].secondaryBg }}>
                <Skeleton
                    variant="text"
                    width="70%"
                    height={isMobile ? 20 : 28}
                    sx={{ bgcolor: theme[themeStyle].grey }}
                />
                <Skeleton
                    variant="text"
                    width="50%"
                    height={isMobile ? 16 : 20}
                    sx={{ bgcolor: theme[themeStyle].grey, mt: 1 }}
                />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 1 }}>
                    <Skeleton
                        variant="circular"
                        width={isMobile ? 14 : 18}
                        height={isMobile ? 14 : 18}
                        sx={{ bgcolor: theme[themeStyle].highlight }}
                    />
                    <Skeleton
                        variant="text"
                        width={isMobile ? 25 : 30}
                        height={isMobile ? 14 : 18}
                        sx={{ bgcolor: theme[themeStyle].grey }}
                    />
                    <Skeleton
                        variant="text"
                        width={isMobile ? 25 : 30}
                        height={isMobile ? 14 : 18}
                        sx={{ bgcolor: theme[themeStyle].grey }}
                    />
                </Stack>
            </Box>
            {/* Actions */}
            <Stack
                direction="row"
                spacing={isMobile ? 1 : 2}
                sx={{
                    p: isMobile ? 1.5 : 2,
                    pt: 0,
                    justifyContent: 'flex-end',
                    bgcolor: theme[themeStyle].secondaryBg,
                }}>
                <Skeleton
                    variant="circular"
                    width={isMobile ? 28 : 36}
                    height={isMobile ? 28 : 36}
                    sx={{ bgcolor: theme[themeStyle].highlight }}
                />
                <Skeleton
                    variant="rectangular"
                    width={isMobile ? 60 : 80}
                    height={isMobile ? 28 : 36}
                    sx={{ borderRadius: 2, bgcolor: theme[themeStyle].grey }}
                />
            </Stack>
        </Card>
    );
};

const TalentResults: React.FC<ITalentCardsProps> = ({
    talents,
    loading,
    selectedCategory = 'talents',
    isMobile = false,
}) => {
    const { themeStyle } = useThemeContext();

    if (loading) {
        return (
            <Box sx={{ width: '100%' }}>
                <Grid container spacing={isMobile ? 1 : 2}>
                    {Array.from({ length: SKELETON_COUNT }).map((_, idx) => (
                        <Grid item xs={6} sm={6} md={4} lg={3} key={idx}>
                            <TalentCardSkeleton isMobile={isMobile} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }
    if (!talents || talents.length === 0) {
        return (
            <Box
                sx={{
                    bgcolor: theme[themeStyle].secondaryBg,
                    color: theme[themeStyle].tag_white,
                    borderRadius: 3,
                    minHeight: 150,
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    boxShadow: 1,
                    display: 'flex',
                    mt: 4,
                }}>
                <Typography
                    sx={{ fontSize: 12, fontWeight: 600, fontFamily: FONT_ENUM.MAIN_MEDIUM, textAlign: 'center' }}>
                    No {selectedCategory.toLowerCase()} found. Adjust your filter & try again.
                </Typography>
            </Box>
        );
    }
    return (
        <Box sx={{ width: '100%' }}>
            <Grid container spacing={isMobile ? 1 : 1}>
                {talents.map(talent => (
                    <Grid item xs={6} sm={6} md={4} lg={3} key={talent._id}>
                        <TalentCard talent={talent} isMobile={isMobile} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default TalentResults;
